'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import pinShowroom from 'assets/images/pin-showroom.png';
import pinPartnerStudio from 'assets/images/pin-partner-studio.png';
import getEnvValue from 'commons/getEnvValue';
import { buildDirectionsUrl, buildDetailUrl, STORE_TYPE_PARTNER } from './helpers';
import { setOptions, importLibrary } from './loadGoogleMaps';
const GOOGLE_MAPS_API_KEY = getEnvValue('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY');

// Owns the map's visual style; theme can be edited in GCP Maps Studio without a code change.
const GOOGLE_MAPS_MAP_ID = '29a117773f6329616ec6e6a0';
const DEFAULT_CENTER = {
  lat: 39.8283,
  lng: -98.5795
};
const DEFAULT_ZOOM = 4;

// Escape store fields so they don't break out of the innerHTML template.
const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};
const esc = value => String(value == null ? '' : value).replace(/[&<>"']/g, c => HTML_ESCAPES[c]);
const buildPopupContent = (store, onClose) => {
  const detailUrl = buildDetailUrl(store);
  const directionsUrl = buildDirectionsUrl(store);
  const title = store.name || `${store.city || ''}, ${store.state || ''}`;
  const addressLines = [store.street, [store.city, store.state, store.zip].filter(Boolean).join(', ')].filter(Boolean).map(line => `<div>${esc(line)}</div>`).join('');
  const trimmedHours = (store.open_hours || '').trim();
  const hoursBlock = trimmedHours ? `<div class="mt-2"><div class="font-bold text-gray-dark">Business Hours</div><div class="whitespace-pre-line">${esc(trimmedHours)}</div></div>` : '';
  const phoneBlock = store.phone ? `<div>${esc(store.phone)}</div>` : '';
  const root = document.createElement('div');
  root.setAttribute('data-iw-build', '1');
  root.className = 'relative pt-1 pl-1 pr-4';
  root.innerHTML = `
    <button
      type="button"
      aria-label="Close"
      class="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center text-gray-dark hover:text-black focus:outline-none"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <path d="M1 1 L13 13 M13 1 L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    </button>
    <div class="font-bold text-base mb-1 pr-7">${esc(title)}</div>
    <div class="text-sm text-gray-light1">${addressLines}${phoneBlock}${hoursBlock}</div>
    <div class="mt-2.5 flex gap-2 text-sm">
      <a href="${esc(directionsUrl)}" target="_blank" rel="noopener noreferrer" class="text-gray-dark underline font-bold">Directions</a>
      <a href="${esc(detailUrl)}" class="text-gray-dark underline font-bold">Details</a>
    </div>
  `;
  root.querySelector('button').addEventListener('click', onClose);
  return root;
};

// Next.js resolves image imports to { src, ... }; webpack resolves them to a string.
const getAssetUrl = asset => typeof asset === 'string' ? asset : asset && asset.src;

// AdvancedMarkerElement anchors the content's bottom-center at the lat/lng,
// so the pin tip must be the bottom-center of this element with no extra Y offset.
// Source pin PNGs are 88x132 (1:1.5) — render at 32x48 to preserve the aspect
// ratio; any other height squishes the pin horizontally.
const buildMarkerContent = isPartner => {
  const img = document.createElement('img');
  img.src = getAssetUrl(isPartner ? pinPartnerStudio : pinShowroom);
  img.alt = '';
  img.style.width = '32px';
  img.style.height = '48px';
  img.style.display = 'block';
  return img;
};
const TAIL_HEIGHT = 11;
const TAIL_TIP_OFFSET = 2;
const popupRemoveListeners = state => {
  if (state.mapClickListener) {
    window.google.maps.event.removeListener(state.mapClickListener);
    state.mapClickListener = null;
  }
  if (state.keydownListener) {
    document.removeEventListener('keydown', state.keydownListener);
    state.keydownListener = null;
  }
};
const popupAutoPan = (state, map) => {
  if (!state.containerEl) return;
  const bubble = state.containerEl.firstChild;
  if (!bubble) return;
  const mapDiv = map.getDiv();
  if (!mapDiv) return;
  const mapRect = mapDiv.getBoundingClientRect();
  const bubbleRect = bubble.getBoundingClientRect();
  let dx = 0;
  let dy = 0;
  const pad = 10;
  if (bubbleRect.left < mapRect.left + pad) {
    dx = bubbleRect.left - mapRect.left - pad;
  } else if (bubbleRect.right > mapRect.right - pad) {
    dx = bubbleRect.right - mapRect.right + pad;
  }
  if (bubbleRect.top < mapRect.top + pad) {
    dy = bubbleRect.top - mapRect.top - pad;
  } else if (bubbleRect.bottom > mapRect.bottom - pad) {
    dy = bubbleRect.bottom - mapRect.bottom + pad;
  }
  if (dx !== 0 || dy !== 0) {
    map.panBy(dx, dy);
  }
};
const createStorePopupClass = () => {
  class StorePopup extends window.google.maps.OverlayView {
    constructor(position, contentEl) {
      super();
      this.state = {
        position,
        contentEl,
        containerEl: null,
        mapClickListener: null,
        keydownListener: null
      };
    }
    onAdd() {
      const {
        state
      } = this;
      const bubble = document.createElement('div');
      bubble.className = 'gm-style-iw-c';
      Object.assign(bubble.style, {
        position: 'absolute',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 7px 1px rgba(0,0,0,0.3)',
        padding: '12px',
        overflow: 'hidden',
        width: 'max-content',
        maxWidth: '320px',
        cursor: 'default',
        userSelect: 'text'
      });
      const content = document.createElement('div');
      content.className = 'gm-style-iw-d';
      Object.assign(content.style, {
        overflow: 'hidden',
        maxWidth: '320px'
      });
      content.appendChild(state.contentEl);
      bubble.appendChild(content);
      const tail = document.createElement('div');
      Object.assign(tail.style, {
        position: 'absolute',
        bottom: `-${TAIL_HEIGHT}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '0',
        height: '0',
        borderLeft: '11px solid transparent',
        borderRight: '11px solid transparent',
        borderTop: `${TAIL_HEIGHT}px solid #fff`,
        pointerEvents: 'none'
      });
      bubble.appendChild(tail);
      const container = document.createElement('div');
      Object.assign(container.style, {
        position: 'absolute',
        cursor: 'auto'
      });
      container.appendChild(bubble);
      state.containerEl = container;
      const panes = this.getPanes();
      panes.floatPane.appendChild(container);
      window.google.maps.OverlayView.preventMapHitsAndGesturesFrom(container);
    }
    draw() {
      const {
        state
      } = this;
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !state.containerEl) return;
      const pos = overlayProjection.fromLatLngToDivPixel(new window.google.maps.LatLng(state.position.lat, state.position.lng));
      if (!pos) return;
      const container = state.containerEl;
      const bubble = container.firstChild;
      const bubbleW = bubble.offsetWidth || 0;
      const bubbleH = bubble.offsetHeight || 0;
      const left = Math.round(pos.x - bubbleW / 2);
      const top = Math.round(pos.y - bubbleH - TAIL_HEIGHT - TAIL_TIP_OFFSET);
      container.style.left = `${left}px`;
      container.style.top = `${top}px`;
    }
    onRemove() {
      const {
        state
      } = this;
      if (state.containerEl && state.containerEl.parentNode) {
        state.containerEl.parentNode.removeChild(state.containerEl);
      }
      state.containerEl = null;
      popupRemoveListeners(state);
    }
    open(map) {
      const {
        state
      } = this;
      this.setMap(map);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          popupAutoPan(state, map);
        });
      });
      state.mapClickListener = map.addListener('click', () => this.close());
      state.keydownListener = e => {
        if (e.key === 'Escape') this.close();
      };
      document.addEventListener('keydown', state.keydownListener);
    }
    close() {
      this.setMap(null);
      popupRemoveListeners(this.state);
    }
  }
  return StorePopup;
};
const StoreLocatorMap = t0 => {
  const $ = _c(15);
  const {
    stores,
    activeStoreId,
    onMarkerClick
  } = t0;
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const markersRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const markerStoreMapRef = useRef(t2);
  const popupRef = useRef(null);
  const StorePopupClassRef = useRef(null);
  const mapsLibRef = useRef(null);
  const coreLibRef = useRef(null);
  const markerLibRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let cancelled = false;
      setOptions({
        key: GOOGLE_MAPS_API_KEY,
        v: "quarterly"
      });
      Promise.all([importLibrary("maps"), importLibrary("core"), importLibrary("marker")]).then(t5 => {
        const [mapsLib, coreLib, markerLib] = t5;
        if (cancelled || !containerRef.current) {
          return;
        }
        mapsLibRef.current = mapsLib;
        coreLibRef.current = coreLib;
        markerLibRef.current = markerLib;
        StorePopupClassRef.current = createStorePopupClass();
        mapRef.current = new mapsLib.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapId: GOOGLE_MAPS_MAP_ID,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });
        setIsReady(true);
      }).catch(err => {
        if (!cancelled) {
          setLoadError(err);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== isReady || $[5] !== onMarkerClick || $[6] !== stores) {
    t5 = () => {
      if (!isReady || !mapRef.current) {
        return;
      }
      const coreLib_0 = coreLibRef.current;
      const markerLib_0 = markerLibRef.current;
      if (!coreLib_0 || !markerLib_0) {
        return;
      }
      if (popupRef.current) {
        popupRef.current.close();
        popupRef.current = null;
      }
      markersRef.current.forEach(_temp);
      markersRef.current = [];
      markerStoreMapRef.current = new Map();
      const validStores = (stores || []).filter(_temp2);
      if (validStores.length === 0) {
        return;
      }
      const bounds = new coreLib_0.LatLngBounds();
      validStores.forEach(store => {
        const position = {
          lat: store.latitude,
          lng: store.longitude
        };
        const marker = new markerLib_0.AdvancedMarkerElement({
          position,
          map: mapRef.current,
          title: store.name || "",
          content: buildMarkerContent(store.type === STORE_TYPE_PARTNER)
        });
        marker.addListener("gmp-click", () => {
          if (popupRef.current) {
            popupRef.current.close();
            popupRef.current = null;
          }
          const StorePopup = StorePopupClassRef.current;
          const popup = new StorePopup(position, buildPopupContent(store, () => {
            popup.close();
            popupRef.current = null;
          }));
          popup.open(mapRef.current);
          popupRef.current = popup;
          if (onMarkerClick) {
            onMarkerClick(store.id);
          }
        });
        markersRef.current.push(marker);
        markerStoreMapRef.current.set(store.id, {
          marker,
          store
        });
        bounds.extend(position);
      });
      if (validStores.length > 1) {
        mapRef.current.fitBounds(bounds);
      } else {
        mapRef.current.setCenter(bounds.getCenter());
        mapRef.current.setZoom(10);
      }
    };
    t6 = [stores, isReady, onMarkerClick];
    $[4] = isReady;
    $[5] = onMarkerClick;
    $[6] = stores;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== activeStoreId || $[10] !== isReady) {
    t7 = () => {
      if (!isReady || activeStoreId == null) {
        return;
      }
      const entry = markerStoreMapRef.current.get(activeStoreId);
      if (!entry) {
        return;
      }
      const {
        store: store_0
      } = entry;
      const position_0 = {
        lat: store_0.latitude,
        lng: store_0.longitude
      };
      if (popupRef.current) {
        popupRef.current.close();
        popupRef.current = null;
      }
      const StorePopup_0 = StorePopupClassRef.current;
      const popup_0 = new StorePopup_0(position_0, buildPopupContent(store_0, () => {
        popup_0.close();
        popupRef.current = null;
      }));
      popup_0.open(mapRef.current);
      popupRef.current = popup_0;
      mapRef.current.panTo(position_0);
    };
    t8 = [activeStoreId, isReady];
    $[9] = activeStoreId;
    $[10] = isReady;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  if (loadError) {
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="w-full h-full min-h-[400px] flex items-center justify-center bg-gray-light7 text-gray-light1 text-center p-4">Map unavailable</div>;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div ref={containerRef} className="w-full h-full min-h-[400px] bg-gray-light7" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
StoreLocatorMap.propTypes = {
  stores: PropTypes.array.isRequired,
  activeStoreId: PropTypes.number,
  onMarkerClick: PropTypes.func
};
export default StoreLocatorMap;
function _temp(m) {
  m.map = null;
}
function _temp2(s) {
  return typeof s.latitude === "number" && typeof s.longitude === "number";
}